import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BarChart3,
  ChevronDown,
  CircleHelp,
  Coins,
  Gauge,
  Gift,
  Grid2X2,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Network,
  RefreshCw,
  ScrollText,
  Search,
  ShieldCheck,
  Blocks,
  TicketCheck,
  UserPlus,
  Trophy,
  Users,
  Wallet,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { shortenAddress } from '../lib/format';
import { useAppData } from '../state/AppDataContext';
import { useWallet } from '../state/WalletContext';
import { Background } from './Background';
import { PageTransition } from './motion';
import { networkLabel } from '../lib/network';

type ShellVariant = 'member' | 'lookup' | 'admin';

interface NavEntry {
  label: string;
  icon: LucideIcon;
  to?: string;
  href?: string;
}

const primaryNav: NavEntry[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard },
  { to: '/join', label: 'Join B-Dynamic', icon: Zap },
  { to: '/slots', label: 'Slots', icon: Grid2X2 },
  { to: '/partners', label: 'Partners', icon: Users },
  { to: '/team', label: 'Generation tree', icon: Network },
  { to: '/transactions', label: 'Transactions', icon: Activity },
  { to: '/statistics', label: 'Statistics', icon: BarChart3 },
  { to: '/lookup', label: 'Lookup', icon: Search },
];

const utilityNav: NavEntry[] = [
  { to: '/tokens', label: 'Utility tokens', icon: TicketCheck },
  { to: '/benefits', label: 'Benefits', icon: Gift },
  { to: '/royal', label: 'Leaders Club', icon: Trophy },
];

const lookupNav: NavEntry[] = [
  { to: '/', label: 'Back to app', icon: LayoutDashboard },
  { to: '/lookup', label: 'Chain explorer', icon: Search },
];

/** User-facing wording for the internal data mode; modes not listed print nothing at all. */
const DATA_MODE_LABEL: Record<string, string> = {
  authenticated: 'Live',
  demo: 'Sample data',
  loading: 'Syncing…',
  stale: 'Stale on-chain data',
  error: 'Connection issue',
};

const adminNav: NavEntry[] = [
  { href: '#admin-explorer', label: 'Member explorer', icon: Users },
  { href: '#admin-overview', label: 'Overview', icon: Gauge },
  { href: '#admin-test-register', label: 'Register test ID', icon: UserPlus },
  { href: '#admin-slot-test', label: 'Slot test tool', icon: Zap },
  { href: '#admin-era-test', label: 'Era test', icon: Zap },
  { href: '#admin-registrations', label: 'Registrations', icon: ListChecks },
  { href: '#admin-engine-settings', label: 'Engine setting (24 levels)', icon: Zap },
  { href: '#admin-slot-report', label: 'Slot distribution', icon: Grid2X2 },
  { href: '#admin-id-browser', label: 'ID browser', icon: Search },
  { href: '#admin-income-report', label: 'Sponsor income', icon: Coins },
  { href: '#admin-missed-income', label: 'Missed income', icon: Gift },
  { href: '#admin-leaders-club', label: 'Leaders Club', icon: Trophy },
  { href: '#admin-engine', label: 'Contract V2 engine', icon: Blocks },
  { href: '#admin-primary-tokens', label: 'Primary tokens', icon: TicketCheck },
  { href: '#admin-token-unfreeze', label: 'Token unfreeze report', icon: TicketCheck },
  { href: '#admin-cdt-benefit', label: 'CDT Benefit', icon: Coins },
  { href: '#admin-treasury', label: 'Treasury & pools', icon: Wallet },
  { href: '#admin-rpc', label: 'RPC & indexer', icon: Network },
  { href: '#admin-audit', label: 'Audit log', icon: ScrollText },
];

function NavItem({ to, href, label, icon: Icon, onNavigate }: NavEntry & { onNavigate(): void }) {
  if (href) {
    return (
      <a
        href={href}
        className="nav-item"
        onClick={(event) => {
          const target = document.querySelector(href);
          if (target) {
            event.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          onNavigate();
        }}
      >
        <Icon size={18} aria-hidden />
        <span>{label}</span>
        <ArrowRight className="nav-arrow" size={14} aria-hidden />
      </a>
    );
  }
  return (
    <NavLink to={to!} end={to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onNavigate}>
      <Icon size={18} aria-hidden />
      <span>{label}</span>
      <ArrowRight className="nav-arrow" size={14} aria-hidden />
    </NavLink>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [idOpen, setIdOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wallet = useWallet();
  const data = useAppData();
  const walletPanel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setWalletOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (walletPanel.current && !walletPanel.current.contains(event.target as Node)) setWalletOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const wrongNetwork = wallet.address && wallet.chainId !== wallet.expectedChainId;
  const activeId = data.activeBdId ?? data.member?.id;
  const member = { id: activeId ?? (data.mode === 'loading' ? 'Loading...' : 'No ID') };
  const healthyRpcCount = data.rpcProviders.filter((provider) => provider.state === 'healthy').length;
  const adminSlug = import.meta.env.VITE_ADMIN_ROUTE_SLUG ?? 'operations-7f3c9a';
  const variant: ShellVariant = location.pathname === `/${adminSlug}` ? 'admin' : location.pathname === '/lookup' ? 'lookup' : 'member';
  const brandSubtitle = variant === 'admin' ? 'OPERATIONS CONSOLE · RESTRICTED' : variant === 'lookup' ? 'CHAIN EXPLORER · PUBLIC DATA' : 'DECENTRALIZED MOMENTUM';
  const navLabel = variant === 'admin' ? 'CONSOLE' : variant === 'lookup' ? 'EXPLORER' : 'NETWORK';
  const activeNav = variant === 'admin' ? adminNav : variant === 'lookup' ? lookupNav : primaryNav;
  const lookupQuery = variant === 'lookup' ? searchParams.get('q') : null;

  return (
    <div className="app-shell" data-shell={variant}>
      <Background />
      <AnimatePresence>
        {menuOpen ? (
          <motion.button
            className="sidebar-backdrop"
            aria-label="Close navigation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenuOpen(false)}
          />
        ) : null}
      </AnimatePresence>

      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="brand-row">
          <NavLink to="/" className="brand" aria-label="B-Dynamic home">
            <span className="brand-mark"><Blocks size={22} /></span>
            <span><strong>B·DYNAMIC</strong><small>{brandSubtitle}</small></span>
          </NavLink>
          <button type="button" className="mobile-close icon-button" onClick={() => setMenuOpen(false)} aria-label="Close navigation">
            <X size={19} />
          </button>
        </div>

        {variant === 'member' ? (
        <div className="id-switcher">
          <button type="button" onClick={() => setIdOpen((value) => !value)} aria-expanded={idOpen}>
            <span className="id-orbit"><span /></span>
            <span><small>ACTIVE ID</small><strong>{member.id}</strong></span>
            <ChevronDown size={15} className={idOpen ? 'rotate' : ''} />
          </button>
          <AnimatePresence>
            {idOpen ? (
              <motion.div className="id-menu" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                {data.memberIds.map((bdId) => <button key={bdId} type="button" onClick={() => { data.selectActiveId(bdId); setIdOpen(false); }}><span>{bdId}</span><small>{bdId === activeId ? 'Active ID' : 'Switch to this ID'}</small></button>)}
                <button type="button" className="add-id" onClick={() => { setIdOpen(false); navigate('/join'); }}><span>+ Add another ID</span><small>Open Join flow</small></button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
        ) : null}

        {variant === 'lookup' ? (
          <div className="lookup-id-badge">
            <span className="id-orbit"><span /></span>
            <span><small>LOOKUP ID</small><strong>{lookupQuery ?? 'Not set'}</strong></span>
          </div>
        ) : null}

        <nav aria-label="Main navigation">
          <span className="nav-label">{navLabel}</span>
          {activeNav.map((item) => <NavItem key={item.to ?? item.href} {...item} onNavigate={() => setMenuOpen(false)} />)}
          {variant === 'member' ? (
            <>
              <span className="nav-label utility-label">UTILITY</span>
              {utilityNav.map((item) => <NavItem key={item.to} {...item} onNavigate={() => setMenuOpen(false)} />)}
            </>
          ) : null}
        </nav>

        <div className="sidebar-foot">
          <div className="security-card">
            {variant === 'admin' ? <LogOut size={18} /> : <ShieldCheck size={18} />}
            <div>
              <strong>{variant === 'admin' ? 'Elevated session' : variant === 'lookup' ? 'Public on-chain data' : wallet.expectedChainId === 56 ? 'Mainnet secured' : 'Testnet secured'}</strong>
              <span>{variant === 'admin' ? 'Every action is audit-logged' : variant === 'lookup' ? 'No wallet required to browse' : 'Verified network guard'}</span>
            </div>
          </div>
          {variant === 'admin' ? <Link to="/"><ArrowRight size={16} /> Exit console</Link> : <a href="mailto:security@bdynamic.example"><CircleHelp size={16} /> Help & security</a>}
        </div>
      </aside>

      <div className="content-shell">
        <header className="topbar">
          <button className="menu-button icon-button" type="button" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu /></button>
          <div className="network-chip">
            <span className="network-dot"><i /></span>
            <span><small>NETWORK</small><strong>{networkLabel(wallet.expectedChainId)}</strong></span>
          </div>
          <div className="topbar-spacer" />
          <NavLink to="/lookup" className="search-shortcut"><Search size={16} /><span>Search anything</span><kbd>/</kbd></NavLink>
          <div className="rpc-pill" title={`${data.rpcProviders.length} RPC providers reported by the API`}><span /><strong>RPC</strong><em>{healthyRpcCount} / {data.rpcProviders.length}</em></div>
          <div className="wallet-wrap" ref={walletPanel}>
            <button
              type="button"
              className={`wallet-button ${wallet.address ? 'connected' : ''}`}
              onClick={() => wallet.address ? setWalletOpen((value) => !value) : void wallet.connect()}
              disabled={wallet.connecting}
            >
              <Wallet size={17} />
              <span>{wallet.connecting ? 'Connecting…' : wallet.address ? shortenAddress(wallet.address) : 'Connect wallet'}</span>
              {wallet.address ? <ChevronDown size={14} /> : null}
            </button>
            <AnimatePresence>
              {walletOpen && wallet.address ? (
                <motion.div className="wallet-popover" initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}>
                  <div className="wallet-avatar"><Wallet /></div>
                  <strong>{shortenAddress(wallet.address, 7)}</strong>
                  <span>{wallet.authenticated ? 'Signed session active' : 'Wallet connected'}</span>
                  {!wallet.authenticated && !wrongNetwork ? <button className="button primary small" type="button" onClick={() => void wallet.authenticate()}>Sign in securely</button> : null}
                  <button className="button ghost small" type="button" onClick={wallet.disconnect}>Disconnect</button>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </header>

        {wrongNetwork ? (
          <div className="network-banner" role="alert">
            <ShieldCheck size={17} />
            <span>Your wallet is on an unsupported network. Transactions remain disabled.</span>
            <button type="button" onClick={() => void wallet.switchNetwork()}>Switch network</button>
          </div>
        ) : null}
        {data.mode === 'stale' ? <div className="network-banner" role="alert"><RefreshCw size={17} /><span>Live refresh failed; showing the last on-chain data that was read successfully.</span><button type="button" onClick={data.refresh}>Retry now</button></div> : null}
        {wallet.error ? <div className="wallet-error" role="alert">{wallet.error}</div> : null}

        <main className="main-content"><PageTransition transitionKey={location.pathname}>{children}</PageTransition></main>
        <footer className="app-footer">
          {/* Never print the raw state-machine value ("empty", "demo", …) at the user. */}
          <span>© 2026 B-Dynamic · {wallet.expectedChainId === 56 ? 'Mainnet' : 'Testnet'}{DATA_MODE_LABEL[data.mode] ? ` · ${DATA_MODE_LABEL[data.mode]}` : ''}</span>
          <span><Coins size={13} /> Economic state is verified on-chain</span>
          <NavLink to="/transactions">View audit trail</NavLink>
        </footer>
      </div>
    </div>
  );
}
